import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { referralApi } from '../../api/referralApi'
import { Referral } from '../../types'
import { toast } from 'react-hot-toast'
import { ArrowLeft, Copy, ExternalLink, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { clsx } from 'clsx'

const STATUS_STYLE: Record<string, string> = {
  REFERRED: 'badge bg-blue-50 text-blue-700',
  ACKNOWLEDGED: 'badge bg-purple-50 text-purple-700',
  HIRED: 'badge bg-green-50 text-green-700',
  CLOSED: 'badge bg-gray-100 text-gray-600',
}

export default function ReferralDetailPage() {
  const { referralId } = useParams<{ referralId: string }>()
  const [referral, setReferral] = useState<Referral | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    referralApi
      .getByReferralId(referralId!)
      .then(({ data }) => setReferral(data.data!))
      .catch(() => toast.error('Referral not found'))
      .finally(() => setLoading(false))
  }, [referralId])

  const copyLink = () => {
    if (referral) {
      navigator.clipboard.writeText(referral.referralLink)
      toast.success('Referral link copied!')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  if (!referral) {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <p className="text-gray-400 text-lg">Referral not found</p>
        <Link to="/" className="btn-primary mt-4 inline-block">Go Home</Link>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto">
      <Link to="/referrals/my" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 no-underline">
        <ArrowLeft size={16} /> Back to My Referrals
      </Link>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900">Referral Details</h1>
          <span className={clsx(STATUS_STYLE[referral.status])}>{referral.status}</span>
        </div>

        {/* Job info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-400 mb-1">Position</p>
          <p className="font-semibold text-gray-900">{referral.jobPost.title}</p>
          <p className="text-sm text-gray-500">{referral.jobPost.company.name}</p>
        </div>

        {/* Referral ID */}
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-1">Referral ID</p>
          <p className="font-mono text-lg font-bold text-primary-600">{referral.referralId}</p>
          <p className="text-xs text-gray-400 mt-1">Use this ID when applying on the company portal</p>
        </div>

        {/* Referral Link */}
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-2">Application Link</p>
          <div className="flex gap-2">
            <a
              href={referral.referralLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-sm text-primary-600 bg-primary-50 px-3 py-2 rounded-lg truncate flex items-center gap-1.5"
            >
              <ExternalLink size={14} /> {referral.referralLink}
            </a>
            <button onClick={copyLink} className="btn-secondary px-3 shrink-0">
              <Copy size={16} />
            </button>
          </div>
        </div>

        {/* Participants */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-gray-400 mb-1">Referred by</p>
            <p className="font-medium text-gray-900">{referral.employee.profile?.displayName || 'Employee'}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-1">Applicant</p>
            <p className="font-medium text-gray-900">{referral.applicant.profile?.displayName || 'You'}</p>
          </div>
        </div>

        {/* Fee info */}
        {referral.referralFeeCredited && (
          <div className="bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm mb-4">
            Referral fee of ₹{referral.feeCreditAmountPaise / 100} has been credited to the employee.
          </div>
        )}

        {/* Date */}
        <p className="text-xs text-gray-400">
          Referred on {format(new Date(referral.createdAt), 'dd MMM yyyy, HH:mm')}
        </p>
      </div>
    </div>
  )
}
